import { DEFAULT_PROJECTS } from '@/data/projects';
import { IDEState, OutputMessage, TabData } from '@/types';
import { trackEvent } from '@/utils/analytics';
import { restoreIDEState } from '@/utils/ide-storage';
import { portfolioStorage } from '@/utils/storage';
import React, { useCallback, useEffect, useState } from 'react';
import { IDEContext, IDEContextType, NavigationHistoryMode } from './ide-context-types';

export const IDEProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [state, setState] = useState<IDEState>(() => {
        const userState = restoreIDEState(portfolioStorage.getIDEState(), DEFAULT_PROJECTS);

        return {
            projects: DEFAULT_PROJECTS, // Always use static project data
            ...userState,
            outputPanel: { ...userState.outputPanel, messages: [], hasInitialized: false },
            modals: {
                isAnyModalOpen: false,
            },
            search: {
                isOpen: false,
            },
        };
    });

    // Save only user-specific state to localStorage
    useEffect(() => {
        const stateToStore = {
            tabs: state.tabs,
            activeTabId: state.activeTabId,
            selectedFile: state.selectedFile,
            selectedProject: state.selectedProject,
            fileExplorer: {
                isVisible: state.fileExplorer.isVisible,
            },
            outputPanel: {
                isVisible: state.outputPanel.isVisible,
                activeTab: state.outputPanel.activeTab,
                // Exclude messages and hasInitialized from storage
            },
            recentFiles: state.recentFiles,
            // Exclude layout from storage - always start with default dimensions
        };
        portfolioStorage.setIDEState(stateToStore);
    }, [state]);

    // Update state with immutability
    const updateState = useCallback((updates: Partial<IDEState>) => {
        setState(prevState => ({ ...prevState, ...updates }));
    }, []);

    // Tab management functions
    const openFile = useCallback((fileId: string, projectId: string) => {
        setState(prevState => {
            // Find the file and project
            const project = prevState.projects.find(p => p.id === projectId);
            const file = project?.files.find(f => f.id === fileId);

            if (!file || !project) return prevState;

            // Check if tab already exists
            const existingTab = prevState.tabs.find(tab => tab.fileId === fileId);
            if (existingTab) {
                // Focus existing tab
                const newState = {
                    ...prevState,
                    tabs: prevState.tabs.map(tab => ({
                        ...tab,
                        isActive: tab.id === existingTab.id,
                    })),
                    activeTabId: existingTab.id,
                };

                // Update URL
                const url = `/${project.name}/${file.name}`;
                window.history.pushState({}, '', url);

                // Track tab switch
                trackEvent('tab_switched', {
                    file_id: fileId,
                    project_id: projectId,
                    file_name: file.name,
                });

                return newState;
            }

            // Create new tab
            const newTab: TabData = {
                id: `tab-${Date.now()}`,
                fileId,
                projectId,
                title: file.name,
                isActive: true,
            };

            // Deactivate all other tabs
            const updatedTabs = prevState.tabs.map(tab => ({
                ...tab,
                isActive: false,
            }));

            // Add new tab and update recent files
            const newTabs = [...updatedTabs, newTab];
            const newRecentFiles = [fileId, ...prevState.recentFiles.filter(id => id !== fileId)].slice(0, 10);

            const newState = {
                ...prevState,
                tabs: newTabs,
                activeTabId: newTab.id,
                recentFiles: newRecentFiles,
            };

            // Update URL
            const url = `/${project.name}/${file.name}`;
            window.history.pushState({}, '', url);

            // Track tab opened
            trackEvent('tab_opened', {
                file_id: fileId,
                project_id: projectId,
                file_name: file.name,
            });

            return newState;
        });
    }, []);

    const closeTab = useCallback((tabId: string) => {
        setState(prevState => {
            const tabIndex = prevState.tabs.findIndex(tab => tab.id === tabId);
            if (tabIndex === -1) return prevState;

            const tabToClose = prevState.tabs[tabIndex];
            const project = prevState.projects.find(p => p.id === tabToClose.projectId);
            const file = project?.files.find(f => f.id === tabToClose.fileId);

            const newTabs = prevState.tabs.filter(tab => tab.id !== tabId);
            let newActiveTabId = prevState.activeTabId;

            // If we're closing the active tab, activate the next one
            if (prevState.activeTabId === tabId) {
                if (newTabs.length > 0) {
                    const nextIndex = Math.min(tabIndex, newTabs.length - 1);
                    const nextTab = newTabs[nextIndex];
                    newActiveTabId = nextTab.id;

                    // Update the isActive property for the new active tab
                    newTabs[nextIndex] = { ...nextTab, isActive: true };
                } else {
                    newActiveTabId = null;
                }
            }

            // Track tab closed
            if (file && project) {
                trackEvent('tab_closed', {
                    file_id: tabToClose.fileId,
                    project_id: tabToClose.projectId,
                    file_name: file.name,
                });
            }

            const newState = {
                ...prevState,
                tabs: newTabs,
                activeTabId: newActiveTabId,
            };

            // Update URL based on remaining tabs
            if (newTabs.length === 0) {
                // No tabs left, go to root
                window.history.pushState({}, '', '/');
            } else if (newActiveTabId) {
                // Update URL to active tab
                const activeTab = newTabs.find(tab => tab.id === newActiveTabId);
                if (activeTab) {
                    const project = prevState.projects.find(p => p.id === activeTab.projectId);
                    const file = project?.files.find(f => f.id === activeTab.fileId);
                    if (project && file) {
                        const url = `/${project.name}/${file.name}`;
                        window.history.pushState({}, '', url);
                    }
                }
            }

            return newState;
        });
    }, []);

    const closeAllTabs = useCallback(() => {
        setState(prevState => ({
            ...prevState,
            tabs: [],
            activeTabId: null,
        }));

        // Update URL to root when all tabs are closed
        window.history.pushState({}, '', '/');
    }, []);

    const closeOtherTabs = useCallback(
        (tabId: string) => {
            const retainedTab = state.tabs.find(tab => tab.id === tabId);
            if (!retainedTab) return;

            const project = state.projects.find(p => p.id === retainedTab.projectId);
            const file = project?.files.find(f => f.id === retainedTab.fileId);

            setState(prevState => {
                const tabToKeep = prevState.tabs.find(tab => tab.id === tabId);
                if (!tabToKeep) return prevState;

                return {
                    ...prevState,
                    tabs: [{ ...tabToKeep, isActive: true }],
                    activeTabId: tabId,
                    selectedFile: { fileId: tabToKeep.fileId, projectId: tabToKeep.projectId },
                    selectedProject: null,
                };
            });

            if (project && file) {
                const url = `/${encodeURIComponent(project.name)}/${encodeURIComponent(file.name)}`;
                if (window.location.pathname !== url) window.history.pushState({}, '', url);
            }
        },
        [state.tabs, state.projects]
    );

    const setActiveTab = useCallback((tabId: string) => {
        setState(prevState => {
            const newState = {
                ...prevState,
                tabs: prevState.tabs.map(tab => ({
                    ...tab,
                    isActive: tab.id === tabId,
                })),
                activeTabId: tabId,
            };

            // Update URL to reflect the new active tab
            const activeTab = newState.tabs.find(tab => tab.id === tabId);
            if (activeTab) {
                const project = newState.projects.find(p => p.id === activeTab.projectId);
                const file = project?.files.find(f => f.id === activeTab.fileId);
                if (project && file) {
                    const url = `/${project.name}/${file.name}`;
                    window.history.pushState({}, '', url);
                }
            }

            return newState;
        });
    }, []);

    // Project management functions
    const toggleProjectExpansion = useCallback((projectId: string) => {
        setState(prevState => ({
            ...prevState,
            projects: prevState.projects.map(project =>
                project.id === projectId ? { ...project, isExpanded: !project.isExpanded } : project
            ),
        }));
    }, []);

    // File explorer functions
    const toggleFileExplorer = useCallback(() => {
        setState(prevState => {
            const newVisibility = !prevState.fileExplorer.isVisible;

            // Track file explorer toggle
            trackEvent('file_explorer_toggled', {
                is_visible: newVisibility,
            });

            return {
                ...prevState,
                fileExplorer: {
                    ...prevState.fileExplorer,
                    isVisible: newVisibility,
                },
            };
        });
    }, []);

    // Output panel functions
    const toggleOutputPanel = useCallback(() => {
        setState(prevState => {
            const newVisibility = !prevState.outputPanel.isVisible;

            // Track output panel toggle
            trackEvent('output_panel_toggled', {
                is_visible: newVisibility,
            });

            return {
                ...prevState,
                outputPanel: {
                    ...prevState.outputPanel,
                    isVisible: newVisibility,
                },
            };
        });
    }, []);

    const setOutputPanelTab = useCallback((tabName: string) => {
        setState(prevState => ({
            ...prevState,
            outputPanel: {
                ...prevState.outputPanel,
                activeTab: tabName,
            },
        }));
    }, []);

    const selectFile = useCallback((fileId: string, projectId: string) => {
        setState(prevState => ({
            ...prevState,
            selectedFile: { fileId, projectId },
            selectedProject: null, // Clear project selection when file is selected
        }));
    }, []);

    const selectProject = useCallback((projectId: string) => {
        setState(prevState => ({
            ...prevState,
            selectedProject: projectId,
            selectedFile: null, // Clear file selection when project is selected
        }));
    }, []);

    // URL Navigation functions
    const expandProject = useCallback((projectId: string) => {
        setState(prevState => ({
            ...prevState,
            projects: prevState.projects.map(project =>
                project.id === projectId ? { ...project, isExpanded: true } : project
            ),
        }));
    }, []);

    const navigateToProject = useCallback((projectName: string, history: NavigationHistoryMode = 'push') => {
        setState(prevState => {
            const project = prevState.projects.find(p => p.name.toLowerCase() === projectName.toLowerCase());
            if (!project) return prevState;

            return {
                ...prevState,
                projects: prevState.projects.map(p => (p.id === project.id ? { ...p, isExpanded: true } : p)),
                selectedProject: project.id,
                selectedFile: null, // Clear file selection when project is selected
            };
        });

        // Update URL
        const url = `/${projectName}`;
        if (history === 'push') window.history.pushState({}, '', url);
    }, []);

    const navigateToFile = useCallback(
        (projectName: string, fileName: string, history: NavigationHistoryMode = 'push') => {
            setState(prevState => {
                const project = prevState.projects.find(p => p.name.toLowerCase() === projectName.toLowerCase());
                if (!project) return prevState;

                const file = project.files.find(f => f.name.toLowerCase() === fileName.toLowerCase());
                if (!file) return prevState;

                // Expand the project
                const updatedProjects = prevState.projects.map(p =>
                    p.id === project.id ? { ...p, isExpanded: true } : p
                );

                // Check if tab already exists
                const existingTab = prevState.tabs.find(tab => tab.fileId === file.id);
                if (existingTab) {
                    // Focus existing tab
                    return {
                        ...prevState,
                        projects: updatedProjects,
                        tabs: prevState.tabs.map(tab => ({
                            ...tab,
                            isActive: tab.id === existingTab.id,
                        })),
                        activeTabId: existingTab.id,
                        selectedFile: { fileId: file.id, projectId: project.id },
                    };
                }

                // Create new tab
                const newTab: TabData = {
                    id: `tab-${Date.now()}`,
                    fileId: file.id,
                    projectId: project.id,
                    title: file.name,
                    isActive: true,
                };

                // Deactivate all other tabs
                const updatedTabs = prevState.tabs.map(tab => ({
                    ...tab,
                    isActive: false,
                }));

                // Add new tab and update recent files
                const newTabs = [...updatedTabs, newTab];
                const newRecentFiles = [file.id, ...prevState.recentFiles.filter(id => id !== file.id)].slice(0, 10);

                return {
                    ...prevState,
                    projects: updatedProjects,
                    tabs: newTabs,
                    activeTabId: newTab.id,
                    selectedFile: { fileId: file.id, projectId: project.id },
                    recentFiles: newRecentFiles,
                };
            });

            // Update URL
            const url = `/${projectName}/${fileName}`;
            if (history === 'push') window.history.pushState({}, '', url);
        },
        []
    );

    const addOutputMessage = useCallback((message: string, type: OutputMessage['type']) => {
        const newMessage: OutputMessage = {
            id: `msg-${Date.now()}`,
            type,
            message,
            timestamp: new Date(),
        };

        setState(prevState => ({
            ...prevState,
            outputPanel: {
                ...prevState.outputPanel,
                messages: [...prevState.outputPanel.messages, newMessage],
            },
        }));
    }, []);

    const markOutputInitialized = useCallback(() => {
        setState(prevState => ({
            ...prevState,
            outputPanel: {
                ...prevState.outputPanel,
                hasInitialized: true,
            },
        }));
    }, []);

    const setModalOpen = useCallback((isOpen: boolean) => {
        setState(prevState => ({
            ...prevState,
            modals: {
                ...prevState.modals,
                isAnyModalOpen: isOpen,
            },
        }));
    }, []);

    const toggleSearch = useCallback(() => {
        setState(prevState => {
            const newIsOpen = !prevState.search.isOpen;

            // Track search opened/closed
            if (newIsOpen) {
                trackEvent('search_opened');
            }

            return {
                ...prevState,
                search: {
                    ...prevState.search,
                    isOpen: newIsOpen,
                },
            };
        });
    }, []);

    const openSearch = useCallback(() => {
        setState(prevState => {
            // Track search opened
            trackEvent('search_opened');

            return {
                ...prevState,
                search: {
                    ...prevState.search,
                    isOpen: true,
                },
            };
        });
    }, []);

    const closeSearch = useCallback(() => {
        setState(prevState => ({
            ...prevState,
            search: {
                ...prevState.search,
                isOpen: false,
            },
        }));
    }, []);

    const value: IDEContextType = {
        state,
        openFile,
        selectFile,
        selectProject,
        closeTab,
        closeAllTabs,
        closeOtherTabs,
        setActiveTab,
        toggleProjectExpansion,
        toggleFileExplorer,
        toggleOutputPanel,
        setOutputPanelTab,
        addOutputMessage,
        markOutputInitialized,
        updateState,
        navigateToFile,
        navigateToProject,
        expandProject,
        setModalOpen,
        toggleSearch,
        openSearch,
        closeSearch,
    };

    return <IDEContext.Provider value={value}>{children}</IDEContext.Provider>;
};
